import { useEffect, useState } from 'react';
import PageContent, { PageBreadcrumbProps } from '../PageContent';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { CTEyeClientModel } from '../../data/entities';
import sharedProperty, { useSharedState } from '../../app/sharedProperty';
import { IconDefinitions, SpinnerDefault, Submenu, SubmenuNavigationItem, SubmenuSideBar } from '../../components';
import { router, routes } from '../../app/routes';
import { UrlService, UserService } from '../../services';
import CTEyeService from '../../services/CTEyeService';
import { Roles } from '../../models';
import { useAppSelector } from '../../app/hooks';
import Forbidden from '../error/Forbidden';

export function CTEyeClientSubpagesWrapper() {
  const location = useLocation();
  const urlParams = useParams();
  const id = UrlService.getParamValue("id", urlParams) ?? "";
  const [data, setData] = useState<CTEyeClientModel | null>(null);
  const params = new Map<string, string>();
  const showSelectModalState = useSharedState(false);
  const title = data?.description ?? "Manage CTEye Client";
  const urlId = (new Map<string, string>()).set('id', id);

  const isAdmin = UserService.isInRole(Roles.admin);
  const canViewAllCompanies = UserService.isInAnyRole([Roles.admin, Roles.staff, Roles.dataAnalyst]);
  const canViewHeartbeats = UserService.isInAnyRole([Roles.admin, Roles.staff, Roles.dataAnalyst]);
  const loggedUser = useAppSelector((state) => state.auth.loggedUser);

  const detailsUrl = router.getUrl(routes.cteyeClientDetails, urlId);
  const heartbeatsUrl = router.getUrl(routes.cteyeClientHeartbeats, urlId);
  const commandsUrl = router.getUrl(routes.cteyeClientQueuedCommands, urlId);
  const uploadSessionsUrl = router.getUrl(routes.cteyeClientUploadSessions, urlId);
  const inspectionHistoryUrl = router.getUrl(routes.cteyeClientInspectionHistory, urlId);
  const liveViewUrl = router.getUrl(routes.cteyeClientLiveView, urlId);

  const menuItems = [
    {
      text: 'Details',
      url: detailsUrl,
      activeMatchUrl: detailsUrl,
      additionalClass: "submenu-sidebar-nav-item",
    },
    ...(canViewHeartbeats ? [
      {
        text: 'Heartbeats',
        url: heartbeatsUrl,
        activeMatchUrl: heartbeatsUrl,
        additionalClass: "submenu-sidebar-nav-item",
      },
    ] : []),
    {
      text: 'Import History',
      url: uploadSessionsUrl,
      activeMatchUrl: uploadSessionsUrl,
      additionalClass: "submenu-sidebar-nav-item",
    },
    {
      text: 'Inspection History',
      url: inspectionHistoryUrl,
      activeMatchUrl: inspectionHistoryUrl,
      additionalClass: "submenu-sidebar-nav-item",
    },
    {
      text: 'Live View',
      url: liveViewUrl,
      activeMatchUrl: liveViewUrl,
      additionalClass: "submenu-sidebar-nav-item",
    },
    ...(isAdmin ? [
      {
        text: 'Queued Commands',
        url: commandsUrl,
        activeMatchUrl: commandsUrl,
        additionalClass: "submenu-sidebar-nav-item",
      },
    ] : []),
  ];

  params.set("id", id ?? "");

  useEffect(() => {
    new CTEyeService()
      .getClientByIdOData(id)
      .then((data) => {
        setData(data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [id]);

  if (data === null) return <SpinnerDefault />;

  if (!canViewAllCompanies && data.companyId !== loggedUser?.company?.id) return <Forbidden />;

  return (
    <PageContent
      title={title}
      breadcrumbs={GetBreadcrumbs(location, data)}
      submenu={<ComponentSubmenu id={id} showSelectModalState={showSelectModalState} isStaffOrAdmin={canViewAllCompanies} />}
      sidemenu={<SubmenuSideBar menuItems={menuItems} />}
    >
      <Outlet />
    </PageContent>
  );
};

interface SubmenuProps {
  id: string,
  showSelectModalState: sharedProperty<boolean>;
  isStaffOrAdmin: boolean;
};

const ComponentSubmenu = (props: SubmenuProps) => {

  const submenus: SubmenuNavigationItem[] = [
    {
      label: "Back",
      icon: IconDefinitions.arrowLeft,
      url: routes.cteye,
    },
  ];

  return <Submenu items={submenus}></Submenu>;
};

const GetBreadcrumbs = (
  location: any,
  data: CTEyeClientModel | undefined | null
): PageBreadcrumbProps => {
  const breadcrumbs: PageBreadcrumbProps = {
    values: [
      { label: "CTEye Clients", url: routes.cteyeRegisteredAgents },
      { label: data?.description ?? "--", url: null }
    ],
  };

  return breadcrumbs;
};

export default CTEyeClientSubpagesWrapper;